import { subHours } from 'date-fns';
import { v4 as uuid } from 'uuid';
import moment from 'moment';

import CourseWork from '../models/CourseWork';

const utils = {

  formatScore(score: number): string {
    if (score % 1) {
      return score.toFixed(2);
    }

    return score.toString();
  },

  offsetDateToUS(date: string): Date {
    const d = new Date(date);
    return subHours(d, 12);
  },

  generateSubmissionUID(course_id: string, assignment_id: string): string {
    const prefix = [course_id, assignment_id].join('-');
    const uid = (() => {
      let value = uuid().substring(prefix.length + 1);
      if (/^-/.test(value)) {
        value = value.substring(1);
        const filler = Math.round(Math.random() * 15).toString(16);
        return filler + value;
      }
      return value;
    })();

    return [prefix, uid].join('-');
  },

  // returns formatted activity id from a submitted file file name
  getActivityIdFromFileName(fileName: string): string {
    const keyArr = fileName.split('.');
    keyArr.pop(); // remove file extension
    const formattedKey = keyArr.join('.');
    return formattedKey.replace(/(\.js$|[^0-9a-zA-Z-().]+)/g, '');
  },

  // returns sanitized activity id (no special characters)
  getSanitizedActivityId(rawActivityId: string): string {
    return rawActivityId.replace(/[^0-9a-zA-Z-().]+/g, '');
  },

  // returns formatted content of submitted file
  getFormattedFileContent(rawContent: string): string {
    return rawContent.replace(/((\\n)+((?!\S+)|(?=})))/g, '\n');
  },

  // swaps 2 children of an array
  swapArrayItems(arr: any[], index1: number, index2: number) {
    const temp = arr[index1];
    arr[index1] = arr[index2];
    arr[index2] = temp;
  },

  // moves array item to different index
  moveArrayItem<ArrayType>(arr: ArrayType[], sourceIndex: number, destinationIndex: number): ArrayType[] {
    const [source] = arr.splice(sourceIndex, 1);
    arr.splice(destinationIndex, 0, source);
    return arr;
  },

  // parses error message from an https request error
  getRequestErrorMessage(error: any, defaultMessage = 'An unknown error occurred.') {
    if (typeof error?.response?.data?.message === 'string')
      return error.response.data.message;

    if (typeof error?.response?.data === 'string' && error.response.data.length < 500)
      return error.response.data;

    if (typeof error?.message === 'string')
      return error.message;

    return defaultMessage;
  },

  getExamDueDateTime(courseWork?: CourseWork | null) {
    if (courseWork && courseWork.dueDate) {
      return moment.utc([
        courseWork.dueDate.year,
        courseWork.dueDate.month - 1,
        courseWork.dueDate.day,
        courseWork.dueTime && courseWork.dueTime.hours ? courseWork.dueTime.hours : 0,
        courseWork.dueTime && courseWork.dueTime.minutes ? courseWork.dueTime.minutes : 0,
      ]);
    }

    return undefined;
  },

  getFormattedExamDue(courseWork?: CourseWork | null) {
    return this.getExamDueDateTime(courseWork)?.format('MMMM DD, YYYY - HH:mm');
  },

  getExamDueUnix(courseWork?: CourseWork | null) {
    return moment(this.getExamDueDateTime(courseWork)).unix() * 1000;
  },

  hasExamEnded(courseWork?: CourseWork | null) {
    return Date.now() > this.getExamDueUnix(courseWork);
  }
};

export default utils;
